import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function JS_Output() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🖥️ JavaScript Output</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        JavaScript can display output in different ways such as writing into the
        HTML page, showing alerts, or logging to the console. Each method is
        useful for different purposes.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>document.write()</code> → writes output directly to HTML (not
            recommended for modern use).
          </li>
          <li>
            <code>innerHTML</code> → modifies content of an HTML element.
          </li>
          <li>
            <code>alert()</code> → displays popup messages.
          </li>
          <li>
            <code>console.log()</code> → logs data in the browser console (best
            for debugging).
          </li>
          <li>
            <code>window.print()</code> → opens the print dialog for printing
            the page.
          </li>
        </ul>
      </div>

      {/* Example 1: document.write */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Using document.write()</h2>
        <p>
          Writes directly into the HTML page. Useful for quick testing, but
          should not be used after the page is fully loaded.
        </p>
        <CodeCopyBlock
          code={`<!DOCTYPE html>
<html>
  <body>
    <script>
      document.write("Hello, this is document.write()");
    </script>
  </body>
</html>`}
        />
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.3rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output: <b>Hello, this is document.write()</b>
        </div>
      </div>

      {/* Example 2: innerHTML */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Using innerHTML</h2>
        <p>Changes the content of an HTML element dynamically.</p>
        <CodeCopyBlock
          code={`<!DOCTYPE html>
<html>
  <body>
    <p id="demo"></p>
    <script>
      document.getElementById("demo").innerHTML = "Hello, innerHTML output!";
    </script>
  </body>
</html>`}
        />
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.3rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>Hello, innerHTML output!</b>
        </div>
      </div>

      {/* Example 3: alert */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Using alert()</h2>
        <p>Shows a popup alert box to the user.</p>
        <CodeCopyBlock
          code={`<!DOCTYPE html>
<html>
  <body>
    <script>
      alert("Hello from JavaScript alert!");
    </script>
  </body>
</html>`}
        />
        <div style={{ marginTop: "1rem" }}>
          <button
            onClick={() => alert("Hello from JavaScript alert!")}
            style={{
              padding: "10px 20px",
              background: "#f59e0b",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Show Alert
          </button>
        </div>
      </div>

      {/* Example 4: console.log */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Using console.log()</h2>
        <p>
          Prints output in the browser console (press <b>F12 → Console</b> to
          view).
        </p>
        <CodeCopyBlock
          code={`<!DOCTYPE html>
<html>
  <body>
    <script>
      console.log("Hello from console.log()");
    </script>
  </body>
</html>`}
        />
        <div
          style={{
            marginTop: "1rem",
            padding: "10px",
            fontSize: "1.1rem",
            background: "#f3f4f6",
            border: "1px solid #9ca3af",
            borderRadius: "8px",
          }}
        >
          Output: <b>Check the browser console</b> (F12 → Console).
        </div>
      </div>

      {/* Example 5: window.print */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Using window.print()</h2>
        <p>Opens the browser’s print dialog to print the current page.</p>
        <CodeCopyBlock
          code={`<!DOCTYPE html>
<html>
  <body>
    <button onclick="window.print()">Print This Page</button>
  </body>
</html>`}
        />
        <div style={{ marginTop: "1rem" }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: "10px 20px",
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Print Page
          </button>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>JavaScript Variables</b> to store data.
          </li>
          <li>
            Understand <b>Data Types</b> and <b>Operators</b>.
          </li>
          <li>
            Practice <b>basic programs</b> using different output methods.
          </li>
        </ul>
      </div>
    </div>
  );
}
